import React from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const ProductsDemo = () => {
  const products = [
    {
      name: "Ostras Vivas Patagónicas",
      description: "Ostras patagónicas frescas, perfectas para una experiencia gourmet auténtica.",
      image: "/images/oyster-4263253_1280.jpg",
      origin: "Patagonia",
      badge: "Premium",
      color: "from-ocean-500 to-jade-500"
    },
    {
      name: "Langostinos L1 Premium", 
      description: "Langostinos de exportación, congelados a bordo. Máxima calidad garantizada.",
      image: "/images/seafood-side-dish-1605699_1280.jpg",
      origin: "Mar Argentino",
      badge: "Exportación",
      color: "from-seagreen-500 to-ocean-500"
    },
    {
      name: "Pack Experiencia Completa",
      description: "Combinación de nuestros mejores productos para una experiencia gastronómica única.",
      image: "/images/grilled-scallops-6609305_1280.jpg",
      origin: "Selección Mar de Jade",
      badge: "Bestseller",
      color: "from-deep-400 to-ocean-500"
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section id="productos" className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-ocean-50 via-pearl-50 to-jade-50" />
      <div className="absolute inset-0 bg-white/70" />
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-crimson text-3xl sm:text-4xl lg:text-5xl font-bold text-deep-700 mb-4 antialiased">
            Productos Premium
          </h2>
          <p className="text-lg text-deep-600 max-w-2xl mx-auto font-dmsans antialiased">
            Mariscos frescos de la más alta calidad, directo del mar a tu mesa
          </p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {products.map((product, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Card className="group overflow-hidden bg-white/90 backdrop-blur-sm border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] h-full">
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${product.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                  
                  {/* Badge */}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/90 text-deep-600 border-0 font-medium px-3 py-1">
                      {product.badge}
                    </Badge>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-deep-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span className="font-dmsans">{product.origin}</span>
                  </div>

                  <CardTitle className="font-crimson text-xl text-deep-700 mb-3 group-hover:text-ocean-600 transition-colors antialiased">
                    {product.name}
                  </CardTitle>

                  <CardDescription className="text-deep-600 mb-6 font-dmsans leading-relaxed antialiased">
                    {product.description}
                  </CardDescription>

                  <Button
                    className="w-full bg-ocean-500 hover:bg-ocean-600 text-white font-medium py-2.5 rounded-full transition-all duration-300 group-hover:shadow-lg"
                    onClick={() => {
                      const element = document.getElementById('contacto');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Consultar Precio
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto border border-white/30 shadow-lg">
            <h3 className="font-crimson text-2xl font-semibold text-deep-700 mb-4 antialiased">
              ¿Buscas algo específico?
            </h3>
            <p className="text-deep-600 mb-6 font-dmsans antialiased">
              Contáctanos para pedidos especiales y consultas personalizadas
            </p>
            <Button
              className="bg-jade-500 hover:bg-jade-600 text-white px-8 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg"
              onClick={() => {
                const element = document.getElementById('contacto');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Contactar Ahora
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsDemo;
